import React, { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import ImageCleanerWidgetClient from '../components/ImageCleanerWidget.client';
import { useLocaleContext } from '../context/LocaleContext';
import { SocialProof } from '../components/SocialProof';
import { generateFAQSchema, injectFAQSchema } from '../utils/faqSchema';
import { RelatedContentForPSEO } from '../components/RelatedContentForPSEO';

import DocumentCleaners from '../components/DocumentCleaners';
import Comparison from '../components/Comparison';
import FeedbackCTA from '../components/FeedbackCTA';

const RelatedTools = lazy(() => import('../components/RelatedTools'));
const RelatedArticles = lazy(() => import('../components/RelatedArticles'));

import {
  Sparkles,
  CheckCircle,
  Shield,
  Zap,
  AlertTriangle,
  ArrowRight,
  Image,
  Search,
  Lock,
  HelpCircle,
  ChevronDown,
  Eye,
  EyeOff,
  Download,
  Camera,
  MapPin,
  Clock,
  FileImage,
  Fingerprint,
  ShieldCheck,
  Lightbulb,
  Target,
  Globe,
} from 'lucide-react';

export default function GeminiImageWatermarkPage() {
  const { user } = useAuth();
  const { locale } = useLocaleContext();
  const [openFAQIndex, setOpenFAQIndex] = React.useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  // Image-specific FAQs - focused on watermark removal and metadata
  const imageFAQs = {
    en: [
      {
        question: "What is the visible watermark on Gemini-generated images?",
        answer: "Google adds a small visible watermark (48x48 or 96x96 pixels) in the bottom-right corner of images generated by Gemini. This watermark appears as a subtle 'G' sparkle logo and is designed to identify AI-generated content. Unlike invisible text watermarks, this is a visible overlay that can be removed using reverse alpha blending techniques. Our tool detects this watermark region automatically and reconstructs the original pixels underneath.",
        icon: Eye,
      },
      {
        question: "What is SynthID and can it be removed?",
        answer: "SynthID is Google DeepMind's invisible watermarking technology that embeds imperceptible signals directly into the pixels of AI-generated images. Unlike the visible corner watermark, SynthID survives editing, screenshots, and compression. Important: Our tool removes the VISIBLE watermark only. SynthID invisible watermarks cannot be removed as they're baked into the image data at generation time.",
        icon: Fingerprint,
      },
      {
        question: "Does this tool strip EXIF metadata and GPS location?",
        answer: "Yes! When you use our image cleaner, we automatically strip ALL metadata from your images including: GPS coordinates (location data), camera information, date/time taken, software used, and any other EXIF tags. This is crucial for privacy - many AI-generated images contain metadata that could reveal information about you or when the image was created. Our preview shows exactly what metadata will be removed before you clean the image. Detection, preview, and metadata stripping are free; downloading the cleaned image requires Premium.",
        icon: MapPin,
      },
      {
        question: "How does the watermark removal algorithm work?",
        answer: "We use Reverse Alpha Blending after a multi-signal detection step. The tool checks current and legacy Gemini layouts, first comparing the expected sparkle shape. On detailed backgrounds it also verifies edge direction, expected white-overlay strength, and whether reversing the blend improves local image continuity. Once detected, it reverses Gemini's alpha-compositing formula to reconstruct the underlying pixels. If confidence remains too low, the image pixels are left unchanged. Processing runs locally in your browser.",
        icon: Lightbulb,
      },
      {
        question: "What image formats are supported?",
        answer: "Our tool supports JPEG, PNG, and WebP formats - the three most common formats used by Google Gemini for image outputs. When you download your cleaned image, it keeps the original format; JPEG and WebP are re-encoded, so small quality differences are possible. PNG images preserve transparency if present. Images up to 20MB are supported, though most Gemini outputs are well under this limit.",
        icon: FileImage,
      },
      {
        question: "Is my image data private and secure?",
        answer: "Absolutely! All processing happens 100% locally in your browser. Your images are NEVER uploaded to any server. We use the Canvas API and Web Workers to process everything client-side. This means: no data leaves your device, we can't see your images, nothing is stored anywhere. This is the safest way to clean sensitive AI-generated images.",
        icon: ShieldCheck,
      },
      {
        question: "Why does Gemini add watermarks to images?",
        answer: "Google adds watermarks for content authenticity and trust. With AI-generated images becoming increasingly realistic, watermarks help: identify AI-generated content to prevent misinformation, maintain transparency about image origins, and comply with emerging AI disclosure regulations. The visible watermark is one layer; SynthID provides a more permanent invisible layer.",
        icon: Target,
      },
      {
        question: "Will removing the watermark affect image quality?",
        answer: "The tool changes pixels only inside a detected watermark region before exporting the image. Reverse alpha blending is designed to reconstruct the underlying colors, but small differences or artifacts can remain, especially when an image has been cropped, resized, captured in a screenshot, or compressed. JPEG and WebP exports may also introduce normal re-encoding differences.",
        icon: Sparkles,
      },
      {
        question: "Which Gemini images does this tool support?",
        answer: "The tool supports observed current and legacy visible-watermark layouts in standard Gemini, Gemini Advanced, Google Workspace, and Imagen outputs that use the same corner sparkle. Because Google can change the watermark size, position, or alpha profile, compatibility with future layouts cannot be guaranteed. Cropping, resizing, screenshots, or heavy recompression can also reduce detection and removal accuracy.",
        icon: Globe,
      },
      {
        question: "How do I know if my image has a Gemini watermark?",
        answer: "Upload your image to our tool! We automatically analyze the bottom-right corner for the characteristic Gemini watermark pattern. Our preview will highlight the detected watermark region with an orange box and show you the watermark size (48x48 or 96x96). If no watermark is detected, we'll let you know. We also display all metadata found in the image so you can see exactly what information will be stripped.",
        icon: Search,
      },
    ],
    de: [
      {
        question: "Was ist das sichtbare Wasserzeichen bei von Gemini generierten Bildern?",
        answer: "Google fügt ein kleines sichtbares Wasserzeichen (48x48 oder 96x96 Pixel) in der unteren rechten Ecke von Gemini-generierten Bildern hinzu. Dieses Wasserzeichen erscheint als dezentes 'G'-Sparkle-Logo und dient zur Identifizierung von KI-generierten Inhalten. Im Gegensatz zu unsichtbaren Text-Wasserzeichen ist dies ein sichtbares Overlay, das mit Reverse-Alpha-Blending-Techniken entfernt werden kann.",
        icon: Eye,
      },
      {
        question: "Was ist SynthID und kann es entfernt werden?",
        answer: "SynthID ist Googles DeepMind unsichtbare Wasserzeichen-Technologie, die unmerkliche Signale direkt in die Pixel von KI-generierten Bildern einbettet. Im Gegensatz zum sichtbaren Eck-Wasserzeichen überlebt SynthID Bearbeitung, Screenshots und Kompression. Wichtig: Unser Tool entfernt nur das SICHTBARE Wasserzeichen. SynthID-unsichtbare Wasserzeichen können nicht entfernt werden, da sie bei der Generierung in die Bilddaten eingebrannt werden.",
        icon: Fingerprint,
      },
      {
        question: "Entfernt dieses Tool EXIF-Metadaten und GPS-Standort?",
        answer: "Ja! Wenn Sie unseren Bild-Cleaner verwenden, entfernen wir automatisch ALLE Metadaten aus Ihren Bildern, einschließlich: GPS-Koordinaten (Standortdaten), Kamerainformationen, Aufnahmedatum/-zeit, verwendete Software und alle anderen EXIF-Tags. Dies ist entscheidend für die Privatsphäre - viele KI-generierte Bilder enthalten Metadaten, die Informationen über Sie preisgeben könnten. Erkennung, Vorschau und Metadaten-Entfernung sind kostenlos; das Herunterladen des bereinigten Bildes erfordert Premium.",
        icon: MapPin,
      },
      {
        question: "Wie funktioniert der Wasserzeichen-Entfernungsalgorithmus?",
        answer: "Wir verwenden Reverse Alpha Blending nach einer mehrstufigen Erkennung. Das Tool prüft aktuelle und ältere Gemini-Layouts und vergleicht zunächst die erwartete Form des Sparkle-Logos. Bei detailreichen Hintergründen prüft es zusätzlich Kantenrichtung, Stärke der weißen Überlagerung und ob die Rückrechnung die lokale Bildkontinuität verbessert. Nach der Erkennung wird die Alpha-Compositing-Formel umgekehrt, um die darunterliegenden Pixel zu rekonstruieren. Bleibt die Konfidenz zu niedrig, bleiben die Bildpixel unverändert. Die Verarbeitung läuft lokal im Browser.",
        icon: Lightbulb,
      },
      {
        question: "Welche Bildformate werden unterstützt?",
        answer: "Unser Tool unterstützt JPEG, PNG und WebP-Formate - die drei häufigsten Formate, die Google Gemini für Bildausgaben verwendet. Beim Download Ihres gereinigten Bildes bleibt das Originalformat erhalten; JPEG und WebP werden neu codiert, sodass kleine Qualitätsunterschiede möglich sind. PNG-Bilder behalten die Transparenz bei. Bilder bis 20MB werden unterstützt.",
        icon: FileImage,
      },
      {
        question: "Sind meine Bilddaten privat und sicher?",
        answer: "Absolut! Die gesamte Verarbeitung erfolgt zu 100% lokal in Ihrem Browser. Ihre Bilder werden NIEMALS auf einen Server hochgeladen. Wir verwenden die Canvas-API und Web Workers für die clientseitige Verarbeitung. Das bedeutet: keine Daten verlassen Ihr Gerät, wir können Ihre Bilder nicht sehen, nichts wird irgendwo gespeichert.",
        icon: ShieldCheck,
      },
      {
        question: "Warum fügt Gemini Wasserzeichen zu Bildern hinzu?",
        answer: "Google fügt Wasserzeichen für Inhaltsauthentizität und Vertrauen hinzu. Mit zunehmend realistischen KI-generierten Bildern helfen Wasserzeichen: KI-generierte Inhalte zu identifizieren, Transparenz über Bildursprünge zu gewährleisten und aufkommende KI-Offenlegungsvorschriften einzuhalten.",
        icon: Target,
      },
      {
        question: "Wird das Entfernen des Wasserzeichens die Bildqualität beeinträchtigen?",
        answer: "Das Tool verändert vor dem Export nur Pixel innerhalb eines erkannten Wasserzeichenbereichs. Reverse Alpha Blending soll die darunterliegenden Farben rekonstruieren, dennoch können kleine Unterschiede oder Artefakte verbleiben – besonders nach Zuschneiden, Skalieren, Screenshots oder Kompression. Beim Export als JPEG oder WebP können außerdem normale Unterschiede durch die Neukodierung entstehen.",
        icon: Sparkles,
      },
      {
        question: "Welche Gemini-Bilder werden unterstützt?",
        answer: "Das Tool unterstützt beobachtete aktuelle und ältere sichtbare Wasserzeichen-Layouts in Gemini, Gemini Advanced, Google Workspace und Imagen-Ausgaben mit demselben Sparkle-Logo in der Ecke. Da Google Größe, Position oder Alpha-Profil ändern kann, kann die Kompatibilität mit zukünftigen Layouts nicht garantiert werden. Zuschneiden, Skalieren, Screenshots oder starke Neukomprimierung können Erkennung und Entfernung ebenfalls beeinträchtigen.",
        icon: Globe,
      },
      {
        question: "Wie erkenne ich, ob mein Bild ein Gemini-Wasserzeichen hat?",
        answer: "Laden Sie Ihr Bild in unser Tool hoch! Wir analysieren automatisch die untere rechte Ecke auf das charakteristische Gemini-Wasserzeichenmuster. Unsere Vorschau hebt den erkannten Wasserzeichenbereich mit einem orangefarbenen Rahmen hervor und zeigt die Wasserzeichengröße (48x48 oder 96x96). Wir zeigen auch alle gefundenen Metadaten an.",
        icon: Search,
      },
    ],
    'pt-BR': [
      {
        question: "O que é a marca d'água visível em imagens geradas pelo Gemini?",
        answer: "O Google adiciona uma pequena marca d'água visível (48x48 ou 96x96 pixels) no canto inferior direito das imagens geradas pelo Gemini. Esta marca d'água aparece como um sutil logotipo 'G' brilhante e é projetada para identificar conteúdo gerado por IA. Ao contrário das marcas d'água de texto invisíveis, esta é uma sobreposição visível que pode ser removida usando técnicas de reverse alpha blending.",
        icon: Eye,
      },
      {
        question: "O que é SynthID e pode ser removido?",
        answer: "SynthID é a tecnologia de marca d'água invisível do Google DeepMind que incorpora sinais imperceptíveis diretamente nos pixels de imagens geradas por IA. Diferente da marca d'água visível no canto, o SynthID sobrevive a edições, capturas de tela e compressão. Importante: Nossa ferramenta remove apenas a marca d'água VISÍVEL. Marcas d'água invisíveis SynthID não podem ser removidas pois são incorporadas nos dados da imagem no momento da geração.",
        icon: Fingerprint,
      },
      {
        question: "Esta ferramenta remove metadados EXIF e localização GPS?",
        answer: "Sim! Quando você usa nosso limpador de imagens, removemos automaticamente TODOS os metadados das suas imagens incluindo: coordenadas GPS (dados de localização), informações da câmera, data/hora de captura, software usado e quaisquer outras tags EXIF. Isso é crucial para a privacidade - muitas imagens geradas por IA contêm metadados que podem revelar informações sobre você. Detecção, prévia e remoção de metadados são gratuitas; baixar a imagem limpa requer Premium.",
        icon: MapPin,
      },
      {
        question: "Como funciona o algoritmo de remoção de marca d'água?",
        answer: "Usamos Reverse Alpha Blending após uma etapa de detecção com vários sinais. A ferramenta verifica layouts atuais e antigos do Gemini, começando pela forma esperada do logotipo brilhante. Em fundos detalhados, também verifica a direção das bordas, a intensidade esperada da sobreposição branca e se a reversão melhora a continuidade local da imagem. Após a detecção, a fórmula de composição alpha é invertida para reconstruir os pixels abaixo da marca. Se a confiança continuar baixa, os pixels permanecem inalterados. Todo o processamento ocorre localmente no navegador.",
        icon: Lightbulb,
      },
      {
        question: "Quais formatos de imagem são suportados?",
        answer: "Nossa ferramenta suporta formatos JPEG, PNG e WebP - os três formatos mais comuns usados pelo Google Gemini para saídas de imagem. Quando você baixa sua imagem limpa, ela mantém o formato original; JPEG e WebP são recodificados, então pequenas diferenças de qualidade são possíveis. Imagens PNG preservam a transparência se presente. Imagens de até 20MB são suportadas.",
        icon: FileImage,
      },
      {
        question: "Meus dados de imagem são privados e seguros?",
        answer: "Absolutamente! Todo o processamento acontece 100% localmente no seu navegador. Suas imagens NUNCA são enviadas para nenhum servidor. Usamos a Canvas API e Web Workers para processar tudo no lado do cliente. Isso significa: nenhum dado sai do seu dispositivo, não podemos ver suas imagens, nada é armazenado em lugar nenhum.",
        icon: ShieldCheck,
      },
      {
        question: "Por que o Gemini adiciona marcas d'água às imagens?",
        answer: "O Google adiciona marcas d'água para autenticidade e confiança do conteúdo. Com imagens geradas por IA se tornando cada vez mais realistas, as marcas d'água ajudam: identificar conteúdo gerado por IA para prevenir desinformação, manter transparência sobre origens das imagens e cumprir regulamentações emergentes de divulgação de IA.",
        icon: Target,
      },
      {
        question: "A remoção da marca d'água afetará a qualidade da imagem?",
        answer: "A ferramenta altera apenas os pixels dentro de uma região de marca d'água detectada antes de exportar a imagem. O Reverse Alpha Blending busca reconstruir as cores abaixo da marca, mas pequenas diferenças ou artefatos podem permanecer, especialmente após cortes, redimensionamento, capturas de tela ou compressão. Exportações JPEG e WebP também podem apresentar diferenças normais de recodificação.",
        icon: Sparkles,
      },
      {
        question: "Quais imagens do Gemini são compatíveis?",
        answer: "A ferramenta é compatível com layouts atuais e antigos de marca d'água visível observados no Gemini, Gemini Advanced, Google Workspace e saídas do Imagen que usam o mesmo brilho no canto. Como o Google pode alterar tamanho, posição ou perfil alpha, não é possível garantir compatibilidade com layouts futuros. Cortes, redimensionamento, capturas de tela ou recompressão intensa também podem reduzir a precisão da detecção e remoção.",
        icon: Globe,
      },
      {
        question: "Como sei se minha imagem tem uma marca d'água do Gemini?",
        answer: "Faça upload da sua imagem para nossa ferramenta! Analisamos automaticamente o canto inferior direito procurando o padrão característico de marca d'água do Gemini. Nossa prévia destacará a região da marca d'água detectada com uma caixa laranja e mostrará o tamanho da marca d'água (48x48 ou 96x96). Também exibimos todos os metadados encontrados na imagem.",
        icon: Search,
      },
    ],
    'ms-MY': [
      {
        question: "Apakah tanda air yang boleh dilihat pada imej yang dijana Gemini?",
        answer: "Google menambah tanda air kecil yang boleh dilihat (48x48 atau 96x96 piksel) di penjuru kanan bawah imej yang dijana oleh Gemini. Tanda air ini muncul sebagai logo 'G' sparkle yang halus dan direka untuk mengenal pasti kandungan yang dijana AI. Tidak seperti tanda air teks yang tidak kelihatan, ini adalah lapisan yang boleh dilihat yang boleh dibuang menggunakan teknik reverse alpha blending.",
        icon: Eye,
      },
      {
        question: "Apakah SynthID dan bolehkah ia dibuang?",
        answer: "SynthID adalah teknologi tanda air tidak kelihatan Google DeepMind yang membenamkan isyarat tidak dapat dilihat terus ke dalam piksel imej yang dijana AI. Tidak seperti tanda air sudut yang boleh dilihat, SynthID bertahan pengeditan, tangkapan skrin dan pemampatan. Penting: Alat kami hanya membuang tanda air YANG BOLEH DILIHAT. Tanda air tidak kelihatan SynthID tidak boleh dibuang kerana ia dimasukkan ke dalam data imej semasa penjanaan.",
        icon: Fingerprint,
      },
      {
        question: "Adakah alat ini membuang metadata EXIF dan lokasi GPS?",
        answer: "Ya! Apabila anda menggunakan pembersih imej kami, kami secara automatik membuang SEMUA metadata daripada imej anda termasuk: koordinat GPS (data lokasi), maklumat kamera, tarikh/masa diambil, perisian yang digunakan dan sebarang tag EXIF yang lain. Ini penting untuk privasi - banyak imej yang dijana AI mengandungi metadata yang boleh mendedahkan maklumat tentang anda. Pengesanan, pratonton dan pembuangan metadata adalah percuma; memuat turun imej bersih memerlukan Premium.",
        icon: MapPin,
      },
      {
        question: "Bagaimana algoritma pembuangan tanda air berfungsi?",
        answer: "Kami menggunakan Reverse Alpha Blending selepas langkah pengesanan berbilang isyarat. Alat ini menyemak susun atur Gemini semasa dan lama, bermula dengan bentuk logo kilauan yang dijangka. Pada latar belakang terperinci, ia turut menyemak arah tepi, kekuatan lapisan putih yang dijangka dan sama ada pembalikan meningkatkan kesinambungan imej setempat. Selepas dikesan, formula komposit alpha dibalikkan untuk membina semula piksel di bawah tanda air. Jika keyakinan masih terlalu rendah, piksel imej dibiarkan tanpa perubahan. Pemprosesan berlaku secara setempat dalam pelayar.",
        icon: Lightbulb,
      },
      {
        question: "Format imej apa yang disokong?",
        answer: "Alat kami menyokong format JPEG, PNG dan WebP - tiga format paling biasa yang digunakan oleh Google Gemini untuk output imej. Apabila anda memuat turun imej yang dibersihkan, ia mengekalkan format asal; JPEG dan WebP dikodkan semula, jadi perbezaan kualiti kecil mungkin berlaku. Imej PNG mengekalkan ketelusan jika ada. Imej sehingga 20MB disokong.",
        icon: FileImage,
      },
      {
        question: "Adakah data imej saya peribadi dan selamat?",
        answer: "Sudah tentu! Semua pemprosesan berlaku 100% secara tempatan dalam pelayar anda. Imej anda TIDAK PERNAH dimuat naik ke mana-mana pelayan. Kami menggunakan Canvas API dan Web Workers untuk memproses semuanya di sisi klien. Ini bermakna: tiada data meninggalkan peranti anda, kami tidak dapat melihat imej anda, tiada apa yang disimpan di mana-mana.",
        icon: ShieldCheck,
      },
      {
        question: "Mengapa Gemini menambah tanda air pada imej?",
        answer: "Google menambah tanda air untuk keaslian kandungan dan kepercayaan. Dengan imej yang dijana AI menjadi semakin realistik, tanda air membantu: mengenal pasti kandungan yang dijana AI untuk mencegah maklumat salah, mengekalkan ketelusan tentang asal usul imej dan mematuhi peraturan pendedahan AI yang muncul.",
        icon: Target,
      },
      {
        question: "Adakah pembuangan tanda air akan menjejaskan kualiti imej?",
        answer: "Alat ini hanya mengubah piksel dalam kawasan tanda air yang dikesan sebelum mengeksport imej. Reverse Alpha Blending direka untuk membina semula warna di bawah tanda air, tetapi perbezaan kecil atau artifak boleh kekal, khususnya selepas pemotongan, penskalaan, tangkapan skrin atau pemampatan. Eksport JPEG dan WebP juga boleh menghasilkan perbezaan biasa akibat pengekodan semula.",
        icon: Sparkles,
      },
      {
        question: "Imej Gemini yang manakah disokong?",
        answer: "Alat ini menyokong susun atur tanda air semasa dan lama yang telah diperhatikan dalam output Gemini, Gemini Advanced, Google Workspace dan Imagen yang menggunakan logo kilauan sudut yang sama. Oleh sebab Google boleh mengubah saiz, kedudukan atau profil alpha, keserasian dengan susun atur masa hadapan tidak boleh dijamin. Pemotongan, penskalaan, tangkapan skrin atau pemampatan semula yang kuat juga boleh mengurangkan ketepatan pengesanan dan pembuangan.",
        icon: Globe,
      },
      {
        question: "Bagaimana saya tahu jika imej saya mempunyai tanda air Gemini?",
        answer: "Muat naik imej anda ke alat kami! Kami secara automatik menganalisis penjuru kanan bawah untuk corak tanda air Gemini yang khas. Pratonton kami akan menyerlahkan kawasan tanda air yang dikesan dengan kotak oren dan menunjukkan saiz tanda air (48x48 atau 96x96). Kami juga memaparkan semua metadata yang terdapat dalam imej.",
        icon: Search,
      },
    ],
    es: [
      {
        question: "¿Qué es la marca de agua visible en las imágenes generadas por Gemini?",
        answer: "Google añade una pequeña marca de agua visible (48x48 o 96x96 píxeles) en la esquina inferior derecha de las imágenes generadas por Gemini. Esta marca de agua aparece como un sutil logotipo 'G' brillante y está diseñada para identificar contenido generado por IA. A diferencia de las marcas de agua de texto invisibles, esta es una superposición visible que puede eliminarse usando técnicas de reverse alpha blending.",
        icon: Eye,
      },
      {
        question: "¿Qué es SynthID y puede eliminarse?",
        answer: "SynthID es la tecnología de marca de agua invisible de Google DeepMind que incrusta señales imperceptibles directamente en los píxeles de imágenes generadas por IA. A diferencia de la marca de agua visible en la esquina, SynthID sobrevive la edición, capturas de pantalla y compresión. Importante: Nuestra herramienta elimina solo la marca de agua VISIBLE. Las marcas de agua invisibles SynthID no pueden eliminarse ya que están integradas en los datos de la imagen en el momento de la generación.",
        icon: Fingerprint,
      },
      {
        question: "¿Esta herramienta elimina metadatos EXIF y ubicación GPS?",
        answer: "¡Sí! Cuando usas nuestro limpiador de imágenes, eliminamos automáticamente TODOS los metadatos de tus imágenes incluyendo: coordenadas GPS (datos de ubicación), información de la cámara, fecha/hora de captura, software usado y cualquier otra etiqueta EXIF. Esto es crucial para la privacidad - muchas imágenes generadas por IA contienen metadatos que podrían revelar información sobre ti. La detección, la vista previa y la eliminación de metadatos son gratuitas; descargar la imagen limpia requiere Premium.",
        icon: MapPin,
      },
      {
        question: "¿Cómo funciona el algoritmo de eliminación de marca de agua?",
        answer: "Usamos Reverse Alpha Blending después de una detección de varias señales. La herramienta comprueba los diseños actuales y anteriores de Gemini, empezando por la forma esperada del logotipo brillante. En fondos detallados también verifica la dirección de los bordes, la intensidad esperada de la superposición blanca y si invertir la mezcla mejora la continuidad local de la imagen. Una vez detectada, se invierte la fórmula de composición alfa para reconstruir los píxeles subyacentes. Si la confianza sigue siendo demasiado baja, los píxeles se dejan sin cambios. Todo el procesamiento ocurre localmente en el navegador.",
        icon: Lightbulb,
      },
      {
        question: "¿Qué formatos de imagen son compatibles?",
        answer: "Nuestra herramienta soporta formatos JPEG, PNG y WebP - los tres formatos más comunes usados por Google Gemini para salidas de imagen. Cuando descargas tu imagen limpia, mantiene el formato original; JPEG y WebP se recodifican, por lo que pueden darse pequeñas diferencias de calidad. Las imágenes PNG preservan la transparencia si está presente. Se admiten imágenes de hasta 20MB.",
        icon: FileImage,
      },
      {
        question: "¿Mis datos de imagen son privados y seguros?",
        answer: "¡Absolutamente! Todo el procesamiento ocurre 100% localmente en tu navegador. Tus imágenes NUNCA se suben a ningún servidor. Usamos la Canvas API y Web Workers para procesar todo del lado del cliente. Esto significa: ningún dato sale de tu dispositivo, no podemos ver tus imágenes, nada se almacena en ningún lugar.",
        icon: ShieldCheck,
      },
      {
        question: "¿Por qué Gemini añade marcas de agua a las imágenes?",
        answer: "Google añade marcas de agua para autenticidad y confianza del contenido. Con las imágenes generadas por IA volviéndose cada vez más realistas, las marcas de agua ayudan: identificar contenido generado por IA para prevenir desinformación, mantener transparencia sobre orígenes de imágenes y cumplir con regulaciones emergentes de divulgación de IA.",
        icon: Target,
      },
      {
        question: "¿Eliminar la marca de agua afectará la calidad de la imagen?",
        answer: "La herramienta cambia píxeles únicamente dentro de una región de marca de agua detectada antes de exportar la imagen. Reverse Alpha Blending intenta reconstruir los colores subyacentes, pero pueden quedar pequeñas diferencias o artefactos, especialmente después de recortar, redimensionar, hacer una captura de pantalla o comprimir la imagen. Las exportaciones JPEG y WebP también pueden introducir diferencias normales de recodificación.",
        icon: Sparkles,
      },
      {
        question: "¿Qué imágenes de Gemini son compatibles?",
        answer: "La herramienta admite los diseños actuales y anteriores de marca de agua visible observados en Gemini, Gemini Advanced, Google Workspace y salidas de Imagen que utilizan el mismo brillo en la esquina. Como Google puede cambiar el tamaño, la posición o el perfil alfa, no se puede garantizar la compatibilidad con diseños futuros. Recortar, redimensionar, hacer capturas de pantalla o recomprimir mucho la imagen también puede reducir la precisión de detección y eliminación.",
        icon: Globe,
      },
      {
        question: "¿Cómo sé si mi imagen tiene una marca de agua de Gemini?",
        answer: "¡Sube tu imagen a nuestra herramienta! Analizamos automáticamente la esquina inferior derecha buscando el patrón característico de marca de agua de Gemini. Nuestra vista previa resaltará la región de marca de agua detectada con un cuadro naranja y mostrará el tamaño de la marca de agua (48x48 o 96x96). También mostramos todos los metadatos encontrados en la imagen.",
        icon: Search,
      },
    ],
    fr: [
      {
        question: "Qu'est-ce que le filigrane visible sur les images générées par Gemini ?",
        answer: "Google ajoute un petit filigrane visible (48x48 ou 96x96 pixels) dans le coin inférieur droit des images générées par Gemini. Ce filigrane apparaît comme un subtil logo 'G' scintillant et est conçu pour identifier le contenu généré par IA. Contrairement aux filigranes de texte invisibles, il s'agit d'une superposition visible qui peut être supprimée en utilisant des techniques de reverse alpha blending. Notre outil détecte automatiquement cette région de filigrane et reconstruit les pixels originaux en dessous.",
        icon: Eye,
      },
      {
        question: "Qu'est-ce que SynthID et peut-il être supprimé ?",
        answer: "SynthID est la technologie de filigrane invisible de Google DeepMind qui intègre des signaux imperceptibles directement dans les pixels des images générées par IA. Contrairement au filigrane visible dans le coin, SynthID survit à l'édition, aux captures d'écran et à la compression. Important : Notre outil supprime uniquement le filigrane VISIBLE. Les filigranes invisibles SynthID ne peuvent pas être supprimés car ils sont intégrés dans les données de l'image au moment de la génération.",
        icon: Fingerprint,
      },
      {
        question: "Cet outil supprime-t-il les métadonnées EXIF et la localisation GPS ?",
        answer: "Oui ! Lorsque vous utilisez notre nettoyeur d'images, nous supprimons automatiquement TOUTES les métadonnées de vos images, y compris : les coordonnées GPS (données de localisation), les informations de l'appareil photo, la date/heure de prise de vue, le logiciel utilisé et toutes les autres balises EXIF. C'est crucial pour la vie privée - de nombreuses images générées par IA contiennent des métadonnées qui pourraient révéler des informations sur vous ou quand l'image a été créée. Notre aperçu montre exactement quelles métadonnées seront supprimées avant que vous ne nettoyiez l'image. La détection, l'aperçu et la suppression des métadonnées sont gratuits ; télécharger l'image nettoyée nécessite Premium.",
        icon: MapPin,
      },
      {
        question: "Comment fonctionne l'algorithme de suppression du filigrane ?",
        answer: "Nous utilisons le Reverse Alpha Blending après une détection à plusieurs signaux. L'outil vérifie les dispositions Gemini actuelles et anciennes, en commençant par la forme attendue du logo scintillant. Sur les arrière-plans détaillés, il vérifie aussi la direction des contours, l'intensité attendue de la superposition blanche et si l'inversion améliore la continuité locale de l'image. Une fois le filigrane détecté, la formule de composition alpha est inversée pour reconstruire les pixels sous-jacents. Si la confiance reste trop faible, les pixels demeurent inchangés. Tout le traitement s'effectue localement dans le navigateur.",
        icon: Lightbulb,
      },
      {
        question: "Quels formats d'image sont pris en charge ?",
        answer: "Notre outil prend en charge les formats JPEG, PNG et WebP - les trois formats les plus courants utilisés par Google Gemini pour les sorties d'images. Lorsque vous téléchargez votre image nettoyée, elle conserve le format d'origine ; JPEG et WebP sont réencodés, de petites différences de qualité sont donc possibles. Les images PNG préservent la transparence si elle est présente. Les images jusqu'à 20 Mo sont prises en charge, bien que la plupart des sorties Gemini soient bien en dessous de cette limite.",
        icon: FileImage,
      },
      {
        question: "Mes données d'image sont-elles privées et sécurisées ?",
        answer: "Absolument ! Tout le traitement se fait à 100% localement dans votre navigateur. Vos images ne sont JAMAIS téléchargées sur un serveur. Nous utilisons l'API Canvas et les Web Workers pour tout traiter côté client. Cela signifie : aucune donnée ne quitte votre appareil, nous ne pouvons pas voir vos images, rien n'est stocké nulle part. C'est le moyen le plus sûr de nettoyer des images sensibles générées par IA.",
        icon: ShieldCheck,
      },
      {
        question: "Pourquoi Gemini ajoute-t-il des filigranes aux images ?",
        answer: "Google ajoute des filigranes pour l'authenticité et la confiance du contenu. Avec des images générées par IA devenant de plus en plus réalistes, les filigranes aident à : identifier le contenu généré par IA pour prévenir la désinformation, maintenir la transparence sur les origines des images et se conformer aux réglementations émergentes de divulgation de l'IA. Le filigrane visible est une couche ; SynthID fournit une couche invisible plus permanente.",
        icon: Target,
      },
      {
        question: "La suppression du filigrane affectera-t-elle la qualité de l'image ?",
        answer: "L'outil modifie uniquement les pixels de la zone de filigrane détectée avant d'exporter l'image. Le Reverse Alpha Blending vise à reconstruire les couleurs sous-jacentes, mais de petites différences ou des artefacts peuvent subsister, surtout après recadrage, redimensionnement, capture d'écran ou compression. Les exports JPEG et WebP peuvent également introduire des différences normales de réencodage.",
        icon: Sparkles,
      },
      {
        question: "Quelles images Gemini sont prises en charge ?",
        answer: "L'outil prend en charge les dispositions actuelles et anciennes de filigrane visible observées dans Gemini, Gemini Advanced, Google Workspace et les sorties Imagen utilisant le même logo scintillant dans le coin. Google pouvant modifier la taille, la position ou le profil alpha, la compatibilité avec de futures dispositions ne peut pas être garantie. Le recadrage, le redimensionnement, les captures d'écran ou une forte recompression peuvent aussi réduire la précision de détection et de suppression.",
        icon: Globe,
      },
      {
        question: "Comment savoir si mon image a un filigrane Gemini ?",
        answer: "Téléchargez votre image dans notre outil ! Nous analysons automatiquement le coin inférieur droit pour détecter le motif de filigrane Gemini caractéristique. Notre aperçu mettra en évidence la région du filigrane détecté avec un cadre orange et affichera la taille du filigrane (48x48 ou 96x96). Si aucun filigrane n'est détecté, nous vous le ferons savoir. Nous affichons également toutes les métadonnées trouvées dans l'image afin que vous puissiez voir exactement quelles informations seront supprimées.",
        icon: Search,
      },
    ],
    ja: [
      {
        question: "Gemini生成画像の可視透かしとは何ですか？",
        answer: "GoogleはGemini生成画像の右下隅に小さな可視透かし（48x48または96x96ピクセル）を追加します。この透かしは微妙な「G」スパークルロゴとして表示され、AI生成コンテンツを識別するために設計されています。見えないテキスト透かしとは異なり、これは目に見えるオーバーレイで、リバースアルファブレンディング技術を使用して削除できます。当社のツールはこの透かし領域を自動的に検出し、下にある元のピクセルを再構築します。",
        icon: Eye,
      },
      {
        question: "SynthIDとは何ですか？削除できますか？",
        answer: "SynthIDは、AI生成画像のピクセルに直接知覚できない信号を埋め込むGoogle DeepMindの不可視透かし技術です。隅の可視透かしとは異なり、SynthIDは編集、スクリーンショット、圧縮に耐えます。重要：当社のツールは可視透かしのみを削除します。SynthIDの不可視透かしは、生成時に画像データに焼き込まれるため削除できません。",
        icon: Fingerprint,
      },
      {
        question: "このツールはEXIFメタデータとGPS位置情報を削除しますか？",
        answer: "はい！画像クリーナーを使用すると、GPS座標（位置データ）、カメラ情報、撮影日時、使用ソフトウェア、その他すべてのEXIFタグを含む、画像からすべてのメタデータを自動的に削除します。これはプライバシーにとって重要です - 多くのAI生成画像には、あなたや画像の作成日時に関する情報を明らかにする可能性のあるメタデータが含まれています。プレビューでは、画像をクリーニングする前に、どのメタデータが削除されるかを正確に表示します。検出、プレビュー、メタデータの削除は無料です。クリーンな画像のダウンロードにはPremiumが必要です。",
        icon: MapPin,
      },
      {
        question: "透かし削除アルゴリズムはどのように機能しますか？",
        answer: "複数の信号による検出後にリバースアルファブレンディングを使用します。ツールは現在および旧版のGemini配置を確認し、まず想定されるスパークルロゴの形状を比較します。複雑な背景では、エッジの方向、白いオーバーレイの想定強度、逆変換によって局所的な画像の連続性が改善するかも確認します。検出後はアルファ合成式を逆算して下のピクセルを再構築します。信頼度が低すぎる場合、画像のピクセルは変更しません。処理はすべてブラウザ内でローカルに実行されます。",
        icon: Lightbulb,
      },
      {
        question: "どの画像フォーマットがサポートされていますか？",
        answer: "当社のツールはJPEG、PNG、WebPフォーマットをサポートしています - Google Geminiが画像出力に使用する最も一般的な3つのフォーマットです。クリーニングされた画像をダウンロードすると、元のフォーマットは維持されますが、JPEGとWebPは再エンコードされるため、わずかな品質の差が生じる場合があります。PNG画像は透明度が存在する場合、それを保持します。20MBまでの画像に対応していますが、ほとんどのGemini出力はこの上限を大きく下回っています。",
        icon: FileImage,
      },
      {
        question: "私の画像データはプライベートで安全ですか？",
        answer: "もちろん！すべての処理はブラウザ内で100%ローカルで行われます。画像はサーバーにアップロードされることはありません。Canvas APIとWeb Workersを使用してすべてをクライアント側で処理します。つまり、データはデバイスを離れず、画像を見ることができず、どこにも保存されません。これは、機密性の高いAI生成画像をクリーニングする最も安全な方法です。",
        icon: ShieldCheck,
      },
      {
        question: "なぜGeminiは画像に透かしを追加するのですか？",
        answer: "Googleはコンテンツの真正性と信頼のために透かしを追加します。AI生成画像がますますリアルになるにつれ、透かしは次のことに役立ちます：誤情報を防ぐためにAI生成コンテンツを識別する、画像の起源についての透明性を維持する、新しいAI開示規制に準拠する。可視透かしは1つの層であり、SynthIDはより永続的な不可視層を提供します。",
        icon: Target,
      },
      {
        question: "透かしを削除すると画像の品質に影響しますか？",
        answer: "このツールは、画像を書き出す前に検出された透かし領域内のピクセルだけを変更します。リバースアルファブレンディングは下の色を再構築するよう設計されていますが、切り抜き、サイズ変更、スクリーンショット、圧縮後の画像では小さな差やアーティファクトが残ることがあります。JPEGやWebPの書き出しでも通常の再エンコード差が生じる場合があります。",
        icon: Sparkles,
      },
      {
        question: "どのGemini画像に対応していますか？",
        answer: "このツールは、Gemini、Gemini Advanced、Google Workspace、Imagenの出力で確認された、同じ隅のスパークルロゴを使用する現在および旧版の可視透かし配置に対応します。Googleがサイズ、位置、アルファプロファイルを変更する可能性があるため、将来の配置への対応は保証できません。切り抜き、サイズ変更、スクリーンショット、強い再圧縮によっても検出と削除の精度が低下する場合があります。",
        icon: Globe,
      },
      {
        question: "画像にGeminiの透かしがあるかどうかを確認する方法は？",
        answer: "当社のツールに画像をアップロードしてください！特徴的なGemini透かしパターンを探して、右下隅を自動的に分析します。プレビューでは、検出された透かし領域をオレンジ色のボックスでハイライトし、透かしのサイズ（48x48または96x96）を表示します。透かしが検出されない場合はお知らせします。また、画像で見つかったすべてのメタデータを表示するので、どの情報が削除されるかを正確に確認できます。",
        icon: Search,
      },
    ],
    id: [
      {
        question: "Apa itu watermark yang terlihat pada gambar yang dihasilkan Gemini?",
        answer: "Google menambahkan watermark kecil yang terlihat (48x48 atau 96x96 piksel) di sudut kanan bawah gambar yang dihasilkan oleh Gemini. Watermark ini muncul sebagai logo 'G' sparkle yang halus dan dirancang untuk mengidentifikasi konten yang dihasilkan AI. Tidak seperti watermark teks yang tidak terlihat, ini adalah overlay yang terlihat yang dapat dihapus menggunakan teknik reverse alpha blending. Alat kami secara otomatis mendeteksi wilayah watermark ini dan merekonstruksi piksel asli di bawahnya.",
        icon: Eye,
      },
      {
        question: "Apa itu SynthID dan bisakah dihapus?",
        answer: "SynthID adalah teknologi watermark tidak terlihat dari Google DeepMind yang menyematkan sinyal yang tidak dapat dirasakan langsung ke dalam piksel gambar yang dihasilkan AI. Tidak seperti watermark sudut yang terlihat, SynthID bertahan dari pengeditan, screenshot, dan kompresi. Penting: Alat kami hanya menghapus watermark YANG TERLIHAT. Watermark tidak terlihat SynthID tidak dapat dihapus karena sudah tertanam dalam data gambar pada saat pembuatan.",
        icon: Fingerprint,
      },
      {
        question: "Apakah alat ini menghapus metadata EXIF dan lokasi GPS?",
        answer: "Ya! Saat Anda menggunakan pembersih gambar kami, kami secara otomatis menghapus SEMUA metadata dari gambar Anda termasuk: koordinat GPS (data lokasi), informasi kamera, tanggal/waktu pengambilan, perangkat lunak yang digunakan, dan tag EXIF lainnya. Ini sangat penting untuk privasi - banyak gambar yang dihasilkan AI mengandung metadata yang dapat mengungkapkan informasi tentang Anda atau kapan gambar dibuat. Pratinjau kami menunjukkan dengan tepat metadata apa yang akan dihapus sebelum Anda membersihkan gambar. Deteksi, pratinjau, dan penghapusan metadata gratis; mengunduh gambar bersih memerlukan Premium.",
        icon: MapPin,
      },
      {
        question: "Bagaimana algoritma penghapusan watermark bekerja?",
        answer: "Kami menggunakan Reverse Alpha Blending setelah tahap deteksi dengan beberapa sinyal. Alat memeriksa tata letak Gemini terbaru dan lama, dimulai dengan bentuk logo kilau yang diharapkan. Pada latar yang rumit, alat juga memeriksa arah tepi, kekuatan overlay putih yang diharapkan, dan apakah pembalikan meningkatkan kontinuitas gambar setempat. Setelah terdeteksi, rumus komposisi alfa dibalik untuk merekonstruksi piksel di bawah watermark. Jika tingkat keyakinan tetap terlalu rendah, piksel gambar dibiarkan tanpa perubahan. Semua pemrosesan berlangsung secara lokal di browser.",
        icon: Lightbulb,
      },
      {
        question: "Format gambar apa yang didukung?",
        answer: "Alat kami mendukung format JPEG, PNG, dan WebP - tiga format paling umum yang digunakan oleh Google Gemini untuk output gambar. Saat Anda mengunduh gambar yang dibersihkan, format aslinya dipertahankan; JPEG dan WebP dikodekan ulang, sehingga perbedaan kualitas kecil mungkin terjadi. Gambar PNG mempertahankan transparansi jika ada. Gambar hingga 20MB didukung, meskipun sebagian besar output Gemini jauh di bawah batas ini.",
        icon: FileImage,
      },
      {
        question: "Apakah data gambar saya pribadi dan aman?",
        answer: "Tentu saja! Semua pemrosesan terjadi 100% secara lokal di browser Anda. Gambar Anda TIDAK PERNAH diunggah ke server mana pun. Kami menggunakan Canvas API dan Web Workers untuk memproses semuanya di sisi klien. Ini berarti: tidak ada data yang meninggalkan perangkat Anda, kami tidak dapat melihat gambar Anda, tidak ada yang disimpan di mana pun. Ini adalah cara paling aman untuk membersihkan gambar sensitif yang dihasilkan AI.",
        icon: ShieldCheck,
      },
      {
        question: "Mengapa Gemini menambahkan watermark ke gambar?",
        answer: "Google menambahkan watermark untuk keaslian dan kepercayaan konten. Dengan gambar yang dihasilkan AI menjadi semakin realistis, watermark membantu: mengidentifikasi konten yang dihasilkan AI untuk mencegah misinformasi, menjaga transparansi tentang asal gambar, dan mematuhi regulasi pengungkapan AI yang muncul. Watermark yang terlihat adalah satu lapisan; SynthID menyediakan lapisan tidak terlihat yang lebih permanen.",
        icon: Target,
      },
      {
        question: "Apakah penghapusan watermark akan mempengaruhi kualitas gambar?",
        answer: "Alat hanya mengubah piksel di dalam wilayah watermark yang terdeteksi sebelum mengekspor gambar. Reverse Alpha Blending dirancang untuk merekonstruksi warna di bawah watermark, tetapi perbedaan kecil atau artefak dapat tetap muncul, terutama setelah pemotongan, perubahan ukuran, tangkapan layar, atau kompresi. Ekspor JPEG dan WebP juga dapat menghasilkan perbedaan normal akibat pengodean ulang.",
        icon: Sparkles,
      },
      {
        question: "Gambar Gemini mana yang didukung?",
        answer: "Alat ini mendukung tata letak watermark terlihat terbaru dan lama yang telah diamati pada keluaran Gemini, Gemini Advanced, Google Workspace, dan Imagen yang menggunakan logo kilau sudut yang sama. Karena Google dapat mengubah ukuran, posisi, atau profil alfa, kompatibilitas dengan tata letak mendatang tidak dapat dijamin. Pemotongan, perubahan ukuran, tangkapan layar, atau kompresi ulang yang berat juga dapat mengurangi akurasi deteksi dan penghapusan.",
        icon: Globe,
      },
      {
        question: "Bagaimana saya tahu apakah gambar saya memiliki watermark Gemini?",
        answer: "Unggah gambar Anda ke alat kami! Kami secara otomatis menganalisis sudut kanan bawah untuk pola watermark Gemini yang khas. Pratinjau kami akan menyorot wilayah watermark yang terdeteksi dengan kotak oranye dan menunjukkan ukuran watermark (48x48 atau 96x96). Jika tidak ada watermark yang terdeteksi, kami akan memberi tahu Anda. Kami juga menampilkan semua metadata yang ditemukan dalam gambar sehingga Anda dapat melihat dengan tepat informasi apa yang akan dihapus.",
        icon: Search,
      },
    ],
  };

  // Inject FAQ Schema JSON-LD for SEO
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const faqSchema = generateFAQSchema(imageFAQs[locale]);
      const cleanup = injectFAQSchema(faqSchema);
      return cleanup;
    } catch (error) {
      console.error('[Gemini Image FAQ] Failed to generate or inject FAQ schema:', error);
    }
  }, [locale]);

  // Localized content
  const content = {
    en: {
      badge: 'Gemini Image Watermark Remover',
      h1: 'Remove Gemini Image Watermarks',
      h1Sub: 'Clean AI Photos in Seconds',
      description: 'Free tool to detect and preview removal of visible watermarks from Google Gemini AI-generated images, and strip EXIF metadata including GPS location. Downloading the cleaned image requires Premium. 100% private - all processing happens in your browser.',
      features: [
        'Removes visible watermark from corner',
        'Strips all metadata (GPS, camera, date)',
        '100% private, nothing is uploaded',
      ],
      cta: 'Clean Your Image Now',
      synthidTitle: 'Understanding Gemini Image Watermarks',
      synthidSubtitle: 'What you need to know about visible and invisible AI watermarks',
      howItWorks: 'How It Works',
      step1: 'Upload Your Image',
      step1Desc: 'Drag & drop or click to select any Gemini-generated image (JPEG, PNG, WebP)',
      step2: 'Preview & Analyze',
      step2Desc: 'See exactly where the watermark is located and what metadata will be stripped',
      step3: 'Download Clean Image',
      step3Desc: 'Watermark removal and metadata stripping are free. Downloading the cleaned image requires Premium.',
      didYouKnow: 'Did You Know?',
      funFacts: [
        'Gemini uses multiple visible-watermark layouts; this tool automatically detects current and legacy positions',
        'Removing the visible corner watermark does not remove invisible SynthID signals',
        'Image metadata may contain location, device, software, and capture-time information',
        'The detector checks current and legacy layouts and leaves image pixels unchanged when confidence is too low',
      ],
      trustBadge1: 'Works Instantly',
      trustBadge2: 'No Data Stored',
    },
    de: {
      badge: 'Gemini Bild-Wasserzeichen-Entferner',
      h1: 'Gemini Bild-Wasserzeichen entfernen',
      h1Sub: 'KI-Fotos in Sekunden bereinigen',
      description: 'Kostenloses Tool zum Erkennen sichtbarer Wasserzeichen in Google Gemini KI-generierten Bildern, mit Vorschau der Entfernung und Löschung von EXIF-Metadaten einschließlich GPS-Standort. Das Herunterladen des bereinigten Bildes erfordert Premium. 100% privat - alle Verarbeitung erfolgt in Ihrem Browser.',
      features: [
        'Entfernt sichtbares Wasserzeichen aus der Ecke',
        'Entfernt alle Metadaten (GPS, Kamera, Datum)',
        '100% privat, nichts wird hochgeladen',
      ],
      cta: 'Jetzt Bild bereinigen',
      synthidTitle: 'Gemini Bild-Wasserzeichen verstehen',
      synthidSubtitle: 'Was Sie über sichtbare und unsichtbare KI-Wasserzeichen wissen müssen',
      howItWorks: 'So funktioniert es',
      step1: 'Bild hochladen',
      step1Desc: 'Ziehen & ablegen oder klicken Sie, um ein Gemini-generiertes Bild auszuwählen (JPEG, PNG, WebP)',
      step2: 'Vorschau & Analyse',
      step2Desc: 'Sehen Sie genau, wo das Wasserzeichen ist und welche Metadaten entfernt werden',
      step3: 'Bereinigtes Bild herunterladen',
      step3Desc: 'Wasserzeichen-Entfernung und Metadaten-Löschung sind kostenlos. Das Herunterladen des bereinigten Bildes erfordert Premium.',
      didYouKnow: 'Wussten Sie schon?',
      funFacts: [
        'Gemini verwendet mehrere sichtbare Wasserzeichen-Layouts; dieses Tool erkennt aktuelle und ältere Positionen automatisch',
        'Das Entfernen des sichtbaren Eck-Wasserzeichens entfernt keine unsichtbaren SynthID-Signale',
        'Bildmetadaten können Standort-, Geräte-, Software- und Aufnahmezeitinformationen enthalten',
        'Der Detektor prüft aktuelle und ältere Layouts und lässt Bildpixel bei zu niedriger Konfidenz unverändert',
      ],
      trustBadge1: 'Funktioniert sofort',
      trustBadge2: 'Keine Daten gespeichert',
    },
    'pt-BR': {
      badge: 'Removedor de Marca d\'Água de Imagem Gemini',
      h1: 'Remover Marca d\'Água de Imagem Gemini',
      h1Sub: 'Limpe Fotos de IA em Segundos',
      description: 'Ferramenta gratuita para detectar marcas d\'água visíveis em imagens geradas por IA do Google Gemini com prévia da remoção, e remover metadados EXIF incluindo localização GPS. Baixar a imagem limpa requer Premium. 100% privado - todo o processamento acontece no seu navegador.',
      features: [
        'Remove marca d\'água visível do canto',
        'Remove todos os metadados (GPS, câmera, data)',
        '100% privado, nada é enviado',
      ],
      cta: 'Limpar Sua Imagem Agora',
      synthidTitle: 'Entendendo Marcas d\'Água de Imagem Gemini',
      synthidSubtitle: 'O que você precisa saber sobre marcas d\'água de IA visíveis e invisíveis',
      howItWorks: 'Como Funciona',
      step1: 'Faça Upload da Sua Imagem',
      step1Desc: 'Arraste & solte ou clique para selecionar qualquer imagem gerada pelo Gemini (JPEG, PNG, WebP)',
      step2: 'Prévia & Análise',
      step2Desc: 'Veja exatamente onde a marca d\'água está localizada e quais metadados serão removidos',
      step3: 'Baixe a Imagem Limpa',
      step3Desc: 'A remoção da marca d\'água e a limpeza de metadados são gratuitas. Baixar a imagem limpa requer Premium.',
      didYouKnow: 'Você Sabia?',
      funFacts: [
        'O Gemini usa vários layouts de marca d\'água visível; esta ferramenta detecta automaticamente posições atuais e antigas',
        'Remover a marca d\'água visível do canto não remove os sinais invisíveis do SynthID',
        'Metadados da imagem podem conter localização, dispositivo, software e horário de captura',
        'O detector verifica layouts atuais e antigos e mantém os pixels inalterados quando a confiança é baixa',
      ],
      trustBadge1: 'Funciona Instantaneamente',
      trustBadge2: 'Nenhum Dado Armazenado',
    },
    'ms-MY': {
      badge: 'Pembuang Tanda Air Imej Gemini',
      h1: 'Buang Tanda Air Imej Gemini',
      h1Sub: 'Bersihkan Foto AI dalam Saat',
      description: 'Alat percuma untuk mengesan tanda air yang boleh dilihat daripada imej yang dijana AI Google Gemini dengan pratonton pembuangan, dan membuang metadata EXIF termasuk lokasi GPS. Memuat turun imej bersih memerlukan Premium. 100% peribadi - semua pemprosesan berlaku dalam pelayar anda.',
      features: [
        'Membuang tanda air yang boleh dilihat dari sudut',
        'Membuang semua metadata (GPS, kamera, tarikh)',
        '100% peribadi, tiada apa dimuat naik',
      ],
      cta: 'Bersihkan Imej Anda Sekarang',
      synthidTitle: 'Memahami Tanda Air Imej Gemini',
      synthidSubtitle: 'Apa yang anda perlu tahu tentang tanda air AI yang boleh dilihat dan tidak boleh dilihat',
      howItWorks: 'Cara Ia Berfungsi',
      step1: 'Muat Naik Imej Anda',
      step1Desc: 'Seret & lepas atau klik untuk memilih mana-mana imej yang dijana Gemini (JPEG, PNG, WebP)',
      step2: 'Pratonton & Analisis',
      step2Desc: 'Lihat dengan tepat di mana tanda air berada dan metadata apa yang akan dibuang',
      step3: 'Muat Turun Imej Bersih',
      step3Desc: 'Pembuangan tanda air dan penghapusan metadata adalah percuma. Memuat turun imej bersih memerlukan Premium.',
      didYouKnow: 'Tahukah Anda?',
      funFacts: [
        'Gemini menggunakan beberapa susun atur tanda air; alat ini mengesan kedudukan semasa dan lama secara automatik',
        'Membuang tanda air sudut yang kelihatan tidak membuang isyarat SynthID yang tidak kelihatan',
        'Metadata imej mungkin mengandungi lokasi, peranti, perisian dan masa tangkapan',
        'Pengesan menyemak susun atur semasa dan lama serta tidak mengubah piksel apabila keyakinan terlalu rendah',
      ],
      trustBadge1: 'Berfungsi Serta-merta',
      trustBadge2: 'Tiada Data Disimpan',
    },
    es: {
      badge: 'Eliminador de Marca de Agua de Imagen Gemini',
      h1: 'Eliminar Marca de Agua de Imagen Gemini',
      h1Sub: 'Limpia Fotos de IA en Segundos',
      description: 'Herramienta gratuita para detectar marcas de agua visibles en imágenes generadas por IA de Google Gemini con vista previa de la eliminación, y eliminar metadatos EXIF incluyendo ubicación GPS. Descargar la imagen limpia requiere Premium. 100% privado - todo el procesamiento ocurre en tu navegador.',
      features: [
        'Elimina marca de agua visible de la esquina',
        'Elimina todos los metadatos (GPS, cámara, fecha)',
        '100% privado, no se sube nada',
      ],
      cta: 'Limpia Tu Imagen Ahora',
      synthidTitle: 'Entendiendo las Marcas de Agua de Imagen Gemini',
      synthidSubtitle: 'Lo que necesitas saber sobre marcas de agua de IA visibles e invisibles',
      howItWorks: 'Cómo Funciona',
      step1: 'Sube Tu Imagen',
      step1Desc: 'Arrastra y suelta o haz clic para seleccionar cualquier imagen generada por Gemini (JPEG, PNG, WebP)',
      step2: 'Vista Previa y Análisis',
      step2Desc: 'Ve exactamente dónde está ubicada la marca de agua y qué metadatos se eliminarán',
      step3: 'Descarga la Imagen Limpia',
      step3Desc: 'La eliminación de la marca de agua y de los metadatos es gratuita. Descargar la imagen limpia requiere Premium.',
      didYouKnow: '¿Sabías Que?',
      funFacts: [
        'Gemini usa varios diseños de marca de agua visible; esta herramienta detecta automáticamente las posiciones actuales y anteriores',
        'Eliminar la marca visible de la esquina no elimina las señales invisibles de SynthID',
        'Los metadatos de imagen pueden contener ubicación, dispositivo, software y hora de captura',
        'El detector comprueba diseños actuales y anteriores y no modifica los píxeles cuando la confianza es baja',
      ],
      trustBadge1: 'Funciona Instantáneamente',
      trustBadge2: 'Sin Datos Almacenados',
    },
    fr: {
      badge: 'Suppresseur de Filigrane Image Gemini',
      h1: 'Supprimer les Filigranes d\'Image Gemini',
      h1Sub: 'Nettoyez vos Photos IA en Quelques Secondes',
      description: 'Outil gratuit pour détecter les filigranes visibles des images générées par l\'IA Google Gemini avec aperçu de la suppression, et supprimer les métadonnées EXIF y compris la localisation GPS. Télécharger l\'image nettoyée nécessite Premium. 100% privé - tout le traitement se fait dans votre navigateur.',
      features: [
        'Supprime le filigrane visible du coin',
        'Supprime toutes les métadonnées (GPS, appareil, date)',
        '100% privé, rien n\'est envoyé',
      ],
      cta: 'Nettoyez Votre Image Maintenant',
      synthidTitle: 'Comprendre les Filigranes d\'Image Gemini',
      synthidSubtitle: 'Ce que vous devez savoir sur les filigranes IA visibles et invisibles',
      howItWorks: 'Comment Ça Fonctionne',
      step1: 'Téléchargez Votre Image',
      step1Desc: 'Glissez-déposez ou cliquez pour sélectionner une image générée par Gemini (JPEG, PNG, WebP)',
      step2: 'Aperçu et Analyse',
      step2Desc: 'Voyez exactement où se trouve le filigrane et quelles métadonnées seront supprimées',
      step3: 'Téléchargez l\'Image Nettoyée',
      step3Desc: 'La suppression du filigrane et l\'effacement des métadonnées sont gratuits. Télécharger l\'image nettoyée nécessite Premium.',
      didYouKnow: 'Le Saviez-Vous ?',
      funFacts: [
        'Gemini utilise plusieurs dispositions de filigrane visible ; cet outil détecte automatiquement les positions actuelles et anciennes',
        'Supprimer le filigrane visible du coin ne supprime pas les signaux SynthID invisibles',
        'Les métadonnées peuvent contenir la localisation, l\'appareil, le logiciel et l\'heure de capture',
        'Le détecteur vérifie les dispositions actuelles et anciennes et ne modifie pas les pixels si la confiance est faible',
      ],
      trustBadge1: 'Fonctionne Instantanément',
      trustBadge2: 'Aucune Donnée Stockée',
    },
    ja: {
      badge: 'Gemini画像透かし削除ツール',
      h1: 'Gemini画像の透かしを削除',
      h1Sub: '数秒でAI写真をクリーンに',
      description: 'Google Gemini AI生成画像から可視透かしを検出し、削除をプレビューできる無料ツール。GPS位置情報を含むEXIFメタデータも削除します。クリーンな画像のダウンロードにはPremiumが必要です。100%プライベート - すべての処理はブラウザ内で完結します。',
      features: [
        'コーナーの可視透かしを削除',
        'すべてのメタデータを削除（GPS、カメラ、日付）',
        '100%プライベート、アップロードなし',
      ],
      cta: '今すぐ画像をクリーンに',
      synthidTitle: 'Gemini画像透かしについて',
      synthidSubtitle: '可視・不可視AI透かしについて知っておくべきこと',
      howItWorks: '使い方',
      step1: '画像をアップロード',
      step1Desc: 'ドラッグ＆ドロップまたはクリックしてGemini生成画像を選択（JPEG、PNG、WebP）',
      step2: 'プレビューと分析',
      step2Desc: '透かしの位置と削除されるメタデータを正確に確認',
      step3: 'クリーンな画像をダウンロード',
      step3Desc: '透かしの削除とメタデータの消去は無料です。クリーンな画像のダウンロードにはPremiumが必要です。',
      didYouKnow: 'ご存知でしたか？',
      funFacts: [
        'Geminiには複数の可視透かし配置があり、このツールは新旧の位置を自動検出します',
        '隅の可視透かしを削除しても、不可視のSynthID信号は削除されません',
        '画像メタデータには位置、端末、ソフトウェア、撮影時刻の情報が含まれる場合があります',
        '検出器は現在と旧版の配置を確認し、信頼度が低すぎる場合は画像のピクセルを変更しません',
      ],
      trustBadge1: '即座に動作',
      trustBadge2: 'データ保存なし',
    },
    id: {
      badge: 'Penghapus Watermark Gambar Gemini',
      h1: 'Hapus Watermark Gambar Gemini',
      h1Sub: 'Bersihkan Foto AI dalam Hitungan Detik',
      description: 'Alat gratis untuk mendeteksi watermark yang terlihat dari gambar yang dihasilkan AI Google Gemini dengan pratinjau penghapusan, dan menghapus metadata EXIF termasuk lokasi GPS. Mengunduh gambar bersih memerlukan Premium. 100% privat - semua pemrosesan terjadi di browser Anda.',
      features: [
        'Menghapus watermark yang terlihat dari sudut',
        'Menghapus semua metadata (GPS, kamera, tanggal)',
        '100% privat, tidak ada yang diunggah',
      ],
      cta: 'Bersihkan Gambar Anda Sekarang',
      synthidTitle: 'Memahami Watermark Gambar Gemini',
      synthidSubtitle: 'Yang perlu Anda ketahui tentang watermark AI yang terlihat dan tidak terlihat',
      howItWorks: 'Cara Kerja',
      step1: 'Unggah Gambar Anda',
      step1Desc: 'Seret & lepas atau klik untuk memilih gambar yang dihasilkan Gemini (JPEG, PNG, WebP)',
      step2: 'Pratinjau & Analisis',
      step2Desc: 'Lihat dengan tepat di mana watermark berada dan metadata apa yang akan dihapus',
      step3: 'Unduh Gambar Bersih',
      step3Desc: 'Penghapusan watermark dan penghapusan metadata gratis. Mengunduh gambar bersih memerlukan Premium.',
      didYouKnow: 'Tahukah Anda?',
      funFacts: [
        'Gemini menggunakan beberapa tata letak watermark; alat ini otomatis mendeteksi posisi terbaru dan lama',
        'Menghapus watermark sudut yang terlihat tidak menghapus sinyal SynthID yang tidak terlihat',
        'Metadata gambar dapat berisi lokasi, perangkat, perangkat lunak, dan waktu pengambilan',
        'Detektor memeriksa tata letak terbaru dan lama serta tidak mengubah piksel saat keyakinan terlalu rendah',
      ],
      trustBadge1: 'Berfungsi Instan',
      trustBadge2: 'Tidak Ada Data Tersimpan',
    },
  };

  const t = content[locale] || content.en;

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950">
      {/* Hero Section - CRO Optimized */}
      <section className="relative overflow-hidden py-16 md:py-20 lg:py-24 min-h-[650px] md:min-h-[700px] lg:min-h-[750px]">
        {/* Background decorative elements - Purple theme for images */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 blur-3xl opacity-20 w-[40rem] h-[40rem]">
            <div className="h-full w-full rounded-full bg-purple-100 dark:bg-purple-900/20"></div>
          </div>
          {/* CLS FIX: Changed from top-1/2 to fixed top-[200px] */}
          <div className="absolute right-0 top-[200px] blur-3xl opacity-20 w-[30rem] h-[30rem]">
            <div className="h-full w-full rounded-full bg-pink-100 dark:bg-pink-800/20"></div>
          </div>
        </div>

        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Content */}
            <div className="space-y-8 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-full text-purple-700 dark:text-purple-300 text-sm font-medium border border-purple-100 dark:border-purple-800">
                <Image size={16} />
                <span>{t.badge}</span>
              </div>

              {/* Main Heading - H1 with primary keyword */}
              <h1 className="font-bold text-neutral-900 dark:text-white leading-tight">
                <span className="block text-4xl md:text-5xl lg:text-6xl text-purple-600 dark:text-purple-400">
                  {t.h1}
                </span>
                <span className="block text-2xl md:text-3xl lg:text-4xl mt-2 text-neutral-700 dark:text-neutral-300">
                  {t.h1Sub}
                </span>
              </h1>

              {/* Description with keywords */}
              <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {t.description}
              </p>

              {/* Trust Badges - CRO */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-900/20 rounded-full text-green-700 dark:text-green-300 text-sm border border-green-200 dark:border-green-800">
                  <Zap size={14} />
                  <span>{t.trustBadge1}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-full text-blue-700 dark:text-blue-300 text-sm border border-blue-200 dark:border-blue-800">
                  <Lock size={14} />
                  <span>{t.trustBadge2}</span>
                </div>
              </div>

              {/* Feature List */}
              <div className="space-y-3 text-left max-w-md mx-auto lg:mx-0">
                {t.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <CheckCircle size={16} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <span className="text-neutral-700 dark:text-neutral-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button - CRO Optimized */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="#remover"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-lg professional-transition shadow-lg hover:shadow-xl active:scale-[0.98]"
                >
                  <Camera size={20} />
                  <span>{t.cta}</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>

              {/* Social Proof */}
              <div className="flex justify-center lg:justify-start pt-4">
                <SocialProof />
              </div>
            </div>

            {/* Right Column - Demo Visual */}
            {/* CLS FIX: min-h reserves space before images load */}
            <div className="relative hidden lg:block min-h-[680px]">
              <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-700 p-6 space-y-4">
                {/* Before/After Demo */}
                <div className="text-center mb-4">
                  <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                    {t.howItWorks}
                  </span>
                </div>

                {/* Before - Real Gemini image with watermark highlighted */}
                {/* CLS FIX: aspect-ratio reserves space before image loads */}
                <div className="relative rounded-xl overflow-hidden shadow-lg" style={{ aspectRatio: '1408/768' }}>
                  <img
                    src="/demo/gemini-before-highlighted-800.webp"
                    srcSet="/demo/gemini-before-highlighted-400.webp 400w, /demo/gemini-before-highlighted-800.webp 800w"
                    sizes="(max-width: 768px) 400px, 800px"
                    alt="Gemini AI generated image with watermark detected"
                    className="w-full h-auto"
                    loading="eager"
                    fetchPriority="high"
                    width={1408}
                    height={768}
                  />
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs font-medium rounded">
                    BEFORE
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex justify-center">
                  {/* CLS FIX: Removed animate-bounce */}
                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full text-white">
                    <ArrowRight size={20} className="rotate-90" />
                  </div>
                </div>

                {/* After - Clean image with watermark removed */}
                {/* CLS FIX: aspect-ratio reserves space before image loads */}
                <div className="relative rounded-xl overflow-hidden border-3 border-green-500 shadow-lg shadow-green-500/20" style={{ aspectRatio: '1408/768' }}>
                  <img
                    src="/demo/gemini-after-800.webp"
                    srcSet="/demo/gemini-after-400.webp 400w, /demo/gemini-after-800.webp 800w"
                    sizes="(max-width: 768px) 400px, 800px"
                    alt="Clean Gemini image with watermark removed"
                    className="w-full h-auto"
                    loading="lazy"
                    width={1408}
                    height={768}
                  />
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-green-600 text-white text-xs font-medium rounded flex items-center gap-1">
                    <CheckCircle size={12} />
                    AFTER - CLEAN
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <div className="text-center p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                    <div className="text-lg font-bold text-green-600 dark:text-green-400">0</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {{
                        en: 'Data Sent',
                        de: 'Gesendete Daten',
                        'pt-BR': 'Dados Enviados',
                        'ms-MY': 'Data Dihantar',
                        es: 'Datos Enviados',
                        fr: 'Données Envoyées',
                        ja: '送信データ',
                        id: 'Data Terkirim',
                      }[locale] || 'Data Sent'}
                    </div>
                  </div>
                  <div className="text-center p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600 dark:text-blue-400">100%</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {{
                        en: 'Private',
                        de: 'Privat',
                        'pt-BR': 'Privado',
                        'ms-MY': 'Peribadi',
                        es: 'Privado',
                        fr: 'Privé',
                        ja: 'プライベート',
                        id: 'Privat',
                      }[locale] || 'Private'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-12">
        {/* Main Image Cleaner Tool */}
        <section id="remover" className="scroll-mt-20">
          <ImageCleanerWidgetClient user={user} />
        </section>

        {/* Did You Know Section - Fun & Educational */}
        <section className="mt-16">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-3xl p-8 md:p-12 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-amber-500 rounded-2xl text-white">
                <Lightbulb className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">
                {t.didYouKnow}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {t.funFacts.map((fact, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white/60 dark:bg-neutral-800/60 rounded-xl"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400 font-bold text-sm">
                    {index + 1}
                  </div>
                  <p className="text-neutral-700 dark:text-neutral-300 text-sm leading-relaxed">
                    {fact}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works - Step by Step */}
        <section className="mt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-4">
              {t.howItWorks}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: t.step1, desc: t.step1Desc, icon: Download, num: '1' },
              { title: t.step2, desc: t.step2Desc, icon: Eye, num: '2' },
              { title: t.step3, desc: t.step3Desc, icon: CheckCircle, num: '3' },
            ].map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-700 h-full">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 font-bold text-xl">
                      {step.num}
                    </div>
                    <step.icon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-800 dark:text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                    {step.desc}
                  </p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-neutral-300 dark:text-neutral-600">
                    <ArrowRight size={24} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Educational Content - SynthID Section */}
        <section className="mt-16">
          <div className="bg-gradient-to-br from-white to-purple-50 dark:from-neutral-800 dark:to-purple-900/20 rounded-3xl shadow-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl text-white">
                <Fingerprint className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-neutral-800 dark:text-white">
                  {t.synthidTitle}
                </h2>
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                  {t.synthidSubtitle}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Visible Watermark */}
              <div className="bg-white dark:bg-neutral-700/50 rounded-xl p-6 border border-neutral-200 dark:border-neutral-600">
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="w-6 h-6 text-orange-500" />
                  <h3 className="text-xl font-semibold text-neutral-800 dark:text-white">
                    {locale === 'de' ? 'Sichtbares Wasserzeichen' : locale === 'pt-BR' ? 'Marca d\'Água Visível' : locale === 'ms-MY' ? 'Tanda Air Boleh Dilihat' : locale === 'es' ? 'Marca de Agua Visible' : locale === 'fr' ? 'Filigrane Visible' : locale === 'ja' ? '可視透かし' : locale === 'id' ? 'Watermark Terlihat' : 'Visible Watermark'}
                  </h3>
                </div>
                <ul className="space-y-3 text-neutral-600 dark:text-neutral-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{locale === 'de' ? '48×48 oder 96×96 Pixel' : locale === 'pt-BR' ? '48×48 ou 96×96 pixels' : locale === 'ms-MY' ? '48×48 atau 96×96 piksel' : locale === 'es' ? '48×48 o 96×96 píxeles' : locale === 'fr' ? '48×48 ou 96×96 pixels' : locale === 'ja' ? '48×48 または 96×96 ピクセル' : locale === 'id' ? '48×48 atau 96×96 piksel' : '48×48 or 96×96 pixels'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{locale === 'de' ? 'Untere rechte Ecke' : locale === 'pt-BR' ? 'Canto inferior direito' : locale === 'ms-MY' ? 'Sudut kanan bawah' : locale === 'es' ? 'Esquina inferior derecha' : locale === 'fr' ? 'Coin inférieur droit' : locale === 'ja' ? '右下コーナー' : locale === 'id' ? 'Sudut kanan bawah' : 'Bottom-right corner'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-green-600 dark:text-green-400 font-medium">{locale === 'de' ? '✓ Kann entfernt werden' : locale === 'pt-BR' ? '✓ Pode ser removido' : locale === 'ms-MY' ? '✓ Boleh dibuang' : locale === 'es' ? '✓ Puede eliminarse' : locale === 'fr' ? '✓ Peut être supprimé' : locale === 'ja' ? '✓ 削除可能' : locale === 'id' ? '✓ Dapat dihapus' : '✓ Can be removed'}</span>
                  </li>
                </ul>
              </div>

              {/* Invisible SynthID */}
              <div className="bg-white dark:bg-neutral-700/50 rounded-xl p-6 border border-neutral-200 dark:border-neutral-600">
                <div className="flex items-center gap-3 mb-4">
                  <EyeOff className="w-6 h-6 text-purple-500" />
                  <h3 className="text-xl font-semibold text-neutral-800 dark:text-white">
                    SynthID ({locale === 'de' ? 'Unsichtbar' : locale === 'pt-BR' ? 'Invisível' : locale === 'ms-MY' ? 'Tidak Kelihatan' : locale === 'es' ? 'Invisible' : locale === 'fr' ? 'Invisible' : locale === 'ja' ? '不可視' : locale === 'id' ? 'Tidak Terlihat' : 'Invisible'})
                  </h3>
                </div>
                <ul className="space-y-3 text-neutral-600 dark:text-neutral-300">
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>{locale === 'de' ? 'In Pixel eingebettet' : locale === 'pt-BR' ? 'Incorporado nos pixels' : locale === 'ms-MY' ? 'Dibenamkan dalam piksel' : locale === 'es' ? 'Incrustado en píxeles' : locale === 'fr' ? 'Intégré dans les pixels' : locale === 'ja' ? 'ピクセルに埋め込まれている' : locale === 'id' ? 'Tertanam dalam piksel' : 'Embedded in pixels'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <span>{locale === 'de' ? 'Überlebt Screenshots & Bearbeitung' : locale === 'pt-BR' ? 'Sobrevive a capturas e edição' : locale === 'ms-MY' ? 'Bertahan tangkapan skrin & pengeditan' : locale === 'es' ? 'Sobrevive capturas y edición' : locale === 'fr' ? 'Survit aux captures d\'écran et à l\'édition' : locale === 'ja' ? 'スクリーンショットと編集に耐える' : locale === 'id' ? 'Bertahan dari screenshot & pengeditan' : 'Survives screenshots & editing'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-red-600 dark:text-red-400 font-medium">{locale === 'de' ? '✗ Kann NICHT entfernt werden' : locale === 'pt-BR' ? '✗ NÃO pode ser removido' : locale === 'ms-MY' ? '✗ TIDAK boleh dibuang' : locale === 'es' ? '✗ NO puede eliminarse' : locale === 'fr' ? '✗ Ne peut PAS être supprimé' : locale === 'ja' ? '✗ 削除不可' : locale === 'id' ? '✗ TIDAK dapat dihapus' : '✗ Cannot be removed'}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mt-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-4">
              {locale === 'de' ? 'Häufig gestellte Fragen' : locale === 'pt-BR' ? 'Perguntas Frequentes' : locale === 'ms-MY' ? 'Soalan Lazim' : locale === 'es' ? 'Preguntas Frecuentes' : locale === 'fr' ? 'Questions Fréquemment Posées' : locale === 'ja' ? 'よくある質問' : locale === 'id' ? 'Pertanyaan yang Sering Diajukan' : 'Frequently Asked Questions'}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
              {locale === 'de' ? 'Alles, was Sie über Gemini Bild-Wasserzeichen wissen müssen' : locale === 'pt-BR' ? 'Tudo que você precisa saber sobre marcas d\'água de imagem Gemini' : locale === 'ms-MY' ? 'Semua yang anda perlu tahu tentang tanda air imej Gemini' : locale === 'es' ? 'Todo lo que necesitas saber sobre marcas de agua de imagen Gemini' : locale === 'fr' ? 'Tout ce que vous devez savoir sur les filigranes d\'image Gemini' : locale === 'ja' ? 'Gemini画像透かしについて知っておくべきすべてのこと' : locale === 'id' ? 'Semua yang perlu Anda ketahui tentang watermark gambar Gemini' : 'Everything you need to know about Gemini image watermarks'}
            </p>
          </div>

          <div className="space-y-4 max-w-3xl mx-auto">
            {imageFAQs[locale]?.map((faq, index) => {
              const IconComponent = faq.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent size={20} className="text-purple-600 dark:text-purple-400" />
                      <span className="font-medium text-neutral-800 dark:text-white">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown
                      size={20}
                      className={`text-neutral-400 transition-transform ${
                        openFAQIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFAQIndex === index && (
                    <div className="px-6 pb-4">
                      <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed pl-8">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Document Cleaners */}
        <DocumentCleaners user={user} />

        {/* Comparison */}
        <Comparison />

        {/* Related Content */}
        <RelatedContentForPSEO
          pageId="gemini-image-watermark"
          pageTitle={t.h1}
          pageDescription={t.description}
          locale={locale}
        />

        {/* Feedback CTA */}
        <FeedbackCTA />
      </div>
    </div>
  );
}
